/**
 * Roster → site-wide table export source: every player matching the page's
 * search + cohort filter (all pages, not just the one on screen), in the
 * table's sort, one row per player, with the table's columns (Jersey, Name,
 * Cohort, Position, School) plus first / last name for mail merges.
 */
import type { ApiClient } from '../../lib/api-client';
import type { Player } from '../../lib/api-types';
import type { ExportColumnDef, TableExportSource } from '../integrations/export-dialog';
import { exportFileStem, fetchAllPages, fileDay } from '../integrations/export-format';

import type { RosterQueryState } from './use-roster';

const COLUMNS: readonly ExportColumnDef[] = [
  { field: 'jersey_number', label: 'Jersey', header: 'Jersey #', isVisible: true },
  { field: 'name', label: 'Name', isVisible: true },
  { field: 'cohort', label: 'Cohort', isVisible: true },
  { field: 'position', label: 'Position', isVisible: true },
  { field: 'school', label: 'School', isVisible: true },
  { field: 'first_name', label: 'First name', isVisible: false },
  { field: 'last_name', label: 'Last name', isVisible: false },
  { field: 'cohort_code', label: 'Cohort code', isVisible: false },
];

export function rosterRowCells(player: Player): (string | null)[] {
  return [
    player.jersey_number,
    player.full_name,
    player.cohort_label ?? player.cohort,
    player.position,
    player.school,
    player.first_name,
    player.last_name,
    player.cohort,
  ];
}

export async function buildRosterExportSource(
  api: ApiClient,
  query: RosterQueryState
): Promise<TableExportSource> {
  const players = await fetchAllPages(
    (page, limit) =>
      api.listPlayers({
        q: query.search === '' ? undefined : query.search,
        cohort: query.cohort === 'all' ? undefined : query.cohort,
        sort_by: query.sortBy,
        sort_order: query.sortOrder,
        page,
        limit,
      }),
    200
  );
  return {
    page: 'roster',
    pageLabel: 'Roster',
    columns: COLUMNS,
    rows: () => players.map(rosterRowCells),
    fileStem: exportFileStem('Roster', null, fileDay(null)),
    sheetName: 'Roster',
    rowNoun: { one: 'player', many: 'players' },
  };
}
